import { Dropdown } from "flowbite-react";
import { Icons } from "../../../../../../utils/icons";
import {
  scheduleInterview,
  updateJobApplication,
} from "../../../../../../services/workspace/recruitment.service";
import CommonModal from "../../../../../components/modals/commonModel";
import { useState } from "react";
import CommonForm from "../../../../../components/form/form";
import toast from "react-hot-toast";

const ProfileCard = ({ data, loading, fetchData, id }) => {
  const [scheduleModal, setScheduleModal] = useState(false);

  if (loading) return <ProfileCardSkeleton />;

  const STATUS_MAP = {
    "Under Review": 0,
    Shortlisted: 1,
    "Scheduled Interview": 2,
    "On Hold": 3,
    "Offer Sent": 4,
    Hired: 5,
    Rejected: 6,
  };

  const statuses = [
    "Under Review",
    "Shortlisted",
    "Scheduled Interview",
    "On Hold",
    "Offer Sent",
    "Hired",
    "Rejected",
  ];

  const updateStatus = async (statusValue) => {
    if (statusValue === 2) {
      setScheduleModal(true);
      return;
    }

    try {
      await updateJobApplication(
        {
          status: statusValue,
        },
        data.uuid
      );

      toast.success("Status updated successfully.");
      // Refresh data after successful update
      if (fetchData) {
        fetchData({}, true, id || data?.uuid || data?.id);
      }
    } catch (err) {
      toast.error("Failed to update status.");
      console.error("Status update error:", err);
    }
  };

  const customCase = {
    0: {
      label: "Pending",
      color: "text-blue-600 text-sm",
      bg: "bg-blue-600",
    },
    1: {
      label: "Shortlisted",
      color: "text-green-600 text-sm",
      bg: "bg-green-600",
    },
    2: {
      label: "Scheduled",
      color: "text-indigo-600 text-sm",
      bg: "bg-indigo-600",
    },
    3: {
      label: "On Hold",
      color: "text-yellow-600 text-sm",
      bg: "bg-yellow-600",
    },
    4: {
      label: "Offered",
      color: "text-purple-600 text-sm",
      bg: "bg-purple-600",
    },
    5: {
      label: "Hired",
      color: "text-emerald-600 text-sm",
      bg: "bg-emerald-600",
    },
    6: {
      label: "Rejected",
      color: "text-red-600 text-sm",
      bg: "bg-red-600",
    },
  };

  // const schedule = async (statusValue, formValues) => {
  //   try {
  //     await scheduleInterview({
  //       job_application_id: newIds,
  //       status: statusValue,
  //       panel: formValues.interview_panel,
  //       date: formValues.interview_date,
  //       time: formValues.interview_time,
  //       mode: formValues.interview_mode,
  //       interviewer_id: formValues.interviewers,
  //       id: id,
  //     });

  //     toast.success("Interview scheduled successfully.");
  //     setSelectedIds([]);
  //     fetchData();
  //   } catch (err) {
  //     toast.error("Failed to schedule interview.");
  //   }
  // };

  const schedule = async (statusValue, formValues) => {
    try {
      await scheduleInterview({
        job_application_id: [data.id],
        status: statusValue,
        interview_link: formValues.interview_link,
        interview_address: formValues.interview_address,
        interview_round_id: formValues.interview_rounded_id,
        date: formValues.interview_date,
        time: formValues.interview_time,
        mode: formValues.interview_mode,
        id: data.jobOpening?.id,
      });

      toast.success("Interview scheduled successfully.");
      setScheduleModal(false);
      // Refresh data after successful scheduling
      if (fetchData) {
        fetchData({}, true, id || data?.uuid || data?.id);
      }
    } catch (err) {
      toast.error("Failed to schedule interview.");
    }
  };

  // Transform rounds to dropdown options
  // Rounds can be at data.jobOpening.rounds or data.rounds depending on API structure
  const rounds = data?.jobOpening?.rounds || data?.rounds || [];
  const roundOptions = rounds.map((round) => ({
    label: round.name || `Round ${round.id}`,
    value: round.id,
  }));

  const scheduleFields = [
    {
      name: "interview_rounded_id",
      label: "Interview Round",
      type: "select",
      placeholder: "Select round",
      options: roundOptions.length > 0 ? roundOptions : [
        { label: "No rounds available", value: "", isDisabled: true },
      ],
      required: true,
      colSpan: 6,
      onChange: (value, formik) => {
        // Automatically set interview_mode when round changes
        formik.setFieldValue("interview_mode", "online");
      },
    },
    {
      name: "interview_mode",
      label: "Interview Mode",
      type: "select",
      placeholder: "Select mode",
      options: [
        { label: "Online", value: "online" },
        { label: "Walk-in", value: "walk-in" },
      ],
      required: true,
      colSpan: 6,
    },
    {
      name: "interview_link",
      label: "Interview Link",
      type: "text",
      colSpan: 12,
      showIf: (values) => values?.interview_mode && values?.interview_mode !== "walk-in", // Show only after mode is selected and not walk-in
    },
    {
      name: "interview_address",
      label: "Interview Address/Platform",
      type: "text",
      colSpan: 12,
      showIf: (values) => values?.interview_mode && values?.interview_mode !== "online", // Show only after mode is selected and not online
    },
    {
      name: "interview_date",
      label: "Interview Date",
      type: "date",
      required: true,
      colSpan: 6,
    },
    {
      name: "interview_time",
      label: "Interview Time",
      type: "time",
      placeholder: "Select time",
      required: true,
      colSpan: 6,
    },
  ];

  return (
    <div className="bg-white p-4 rounded-md flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
      <div className="flex flex-row items-center gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 rounded-full flex items-center justify-center border text-gray-500">
          {Icons.USER}
        </div>

        <div className="flex flex-col gap-1">
          {/* Candidate Name */}
          <p className="text-lg font-semibold text-theme">
            {data?.name || "-"}
          </p>

          <div className="flex flex-wrap items-center gap-6 text-sm">
            {/* Applied For */}
            <span className="flex flex-row gap-2">
              <p className="text-theme font-bold">Applied For:</p>
              <p className="text-textLight">{data?.jobOpening?.title || "-"}</p>
            </span>

            {/* Experience */}
            <span className="flex flex-row gap-2">
              <p className="text-theme font-bold">Experience:</p>
              <p className="text-textLight">
                {data?.experience ? `${data.experience} yrs` : "-"}
              </p>
            </span>

            {/* Current Role */}
            <span className="flex flex-row gap-2">
              <p className="text-theme font-bold">Current Role:</p>
              <p className="text-textLight">{data?.current_role || "-"}</p>
            </span>

            {/* Phone */}
            <span className="flex flex-row gap-2">
              <p className="text-theme font-bold">Phone:</p>
              <p className="text-textLight">{data?.phone_number || "-"}</p>
            </span>
            {/* Status */}
            <span className="flex flex-row gap-2 items-center">
              <p className="text-theme font-bold">Status:</p>

              {customCase[data?.status] ? (
                <span
                  className={`${customCase[data.status].color} font-semibold`}
                >
                  {customCase[data.status].label}
                </span>
              ) : (
                <span className="text-textLight">-</span>
              )}
            </span>
          </div>
        </div>
      </div>
      {data?.status !== 5 && (
      <Dropdown
        label={"Update Status"}
        className={
          "hover:border border border-gray-100 md:w-[180px] w-[180px] bg-white pb-0 shadow-custom rounded-md z-30"
        }
        // renderTrigger={() => trigger}
        dismissOnClick={true}
        placement="bottom-center"
      >
        <div className="flex flex-col">
          {statuses.map((item, index) => (
            <Dropdown.Item
              key={index}
              className="hover:bg-transparent focus:bg-transparent hover:rounded-md text-theme"
            >
              <div
                className="flex justify-between items-center w-full cursor-pointer gap-2 "
                onClick={() => updateStatus(STATUS_MAP[item])}
              >
                <span className="text-sm">{item}</span>
              </div>
            </Dropdown.Item>
          ))}
        </div>

        {/* </div> */}
      </Dropdown>
      )}

      <CommonModal
        show={scheduleModal}
        onClose={() => setScheduleModal(false)}
        modalSize="xl"
        headerTitle={"Interview Schedule"}
      >
        <CommonForm
          fields={scheduleFields}
          onSubmit={(values) => {
            schedule(STATUS_MAP["Scheduled Interview"], values);
            setScheduleModal(false);
          }}
          submitButtonText="Schedule"
        />
      </CommonModal>
    </div>
  );
};

const ProfileCardSkeleton = () => (
  <div className="flex flex-row justify-between items-center bg-white p-4 rounded-md animate-pulse">
    <div className="flex items-center flex-row gap-4">
      <div className="rounded-full bg-gray-200 w-12 h-12" />
      <span className="flex flex-col gap-2 w-full">
        <span className="flex flex-row gap-4 items-center">
          <div className="h-5 w-32 bg-gray-200 rounded" />
        </span>
        <span className="flex flex-row gap-10">
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
        </span>
      </span>
    </div>
  </div>
);

export default ProfileCard;
